import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, AlertCircle, Hash } from 'lucide-react';

/**
 * Components
 */
import BaseButton from 'components/base-button/base-button';
import API from "../../utils/api"
import useModal from "../../hooks/use-modal"

const FormNewAccount = (props) => {

    const [refNumber, setRefNumber] = useState('');
    const [error, setError] = useState();
    const [loading, setLoading] = useState();
    const { closeModal } = useModal();

    const handleSubmit = async (e) => {
		    e.preventDefault();

        // Link accounts
        setLoading(true);
        const linkReq = {RefId:refNumber};
        const linkResponse = await API.post("/accounts",linkReq);
        setLoading(false);
        if (linkResponse.data.result_code && linkResponse.data.result_code !== 0) {
            setError(linkResponse.data.message);
        } else {
            props.fetchAccounts();
            closeModal();
        }
    };

    return (
    <form onSubmit={handleSubmit} className="space-y-6">
        {/* Information Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4"
        >
          <p className="text-sm text-gray-300">
            Enter your reference number to link your account. This number should have been provided to you by your creditor or collection agency.
          </p>
        </motion.div>

        {/* Input Field */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <label htmlFor="ref-number" className="block text-sm font-medium text-gray-300 mb-2">
            Reference Number
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Hash className="h-5 w-5 text-gray-500" />
            </div>
            <input
              id="ref-number"
              type="text"
              value={refNumber}
              onChange={e => setRefNumber(e.target.value)}
              placeholder="Enter your reference number"
              className="w-full pl-10 pr-3 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              required
            />
          </div>
        </motion.div>

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3"
            role="alert"
          >
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-300">{error}</p>
          </motion.div>
        )}

        {/* Submit Button */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <BaseButton
            type="submit"
            className="w-full py-3 rounded-xl font-semibold text-base bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2"
            loading={loading}
            disabled={loading || !refNumber.trim()}
            tabIndex={3}
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Looking up account...
              </>
            ) : (
              <>
                <Search className="w-5 h-5" />
                Lookup Accounts
              </>
            )}
          </BaseButton>
        </motion.div>

        {/* Help Text */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="text-center"
        >
          <p className="text-xs text-gray-500">
            Need help? Contact our support team if you're having trouble finding your reference number.
          </p>
        </motion.div>
    </form>
)}

export default FormNewAccount;
